import {
  EventDate,
  useShowCampaignById_new,
  useShowCampaignSettings,
  useShowEventDate,
} from '@givebacks/api/store';
import { useFormListValidator } from 'core';
import { DateTime } from 'luxon';
import {
  AccordionCard,
  Box,
  DatePickerInput,
  Divider,
  Group,
  LoadingOverlay,
  SimpleGrid,
  Text,
  TextInput,
  Textarea,
  TimeInput,
  Tooltip,
} from 'ui';
import { isNotEmpty, useForm } from 'ui/Form';
import { IconInfo } from 'ui/Icons';

import { useAddEventDate } from './AddEventDate.context';
import { EventDateFormType } from './EventDateForm.types';
import { EventDateTicketsMultiSelect } from './EventDateTicketsMultiSelect';

const AddEditEventDateForm = ({ eventDate }: { eventDate?: EventDate }) => {
  const { causeId, eventId, isDuplicate } = useAddEventDate();
  const { data: campaignSettings } = useShowCampaignSettings({
    campaignId: eventId,
    causeId,
  });

  const { data: campaign } = useShowCampaignById_new({ causeId, campaignId: eventId });
  const startingDate = DateTime.fromISO(eventDate?.starting_at ?? '');
  const endingDate = DateTime.fromISO(eventDate?.ending_at ?? '');

  const selectedTickets = eventDate?.event_date_tickets?.map((ticket) => ticket.store_item_id);

  const form = useForm<EventDateFormType>({
    initialValues: {
      title: isDuplicate ? `${eventDate?.title} (Copy)` : (eventDate?.title ?? ''),
      date: startingDate.isValid ? startingDate.toJSDate() : null,
      starting_at: startingDate.isValid ? startingDate.toFormat('TT') : '08:00:00',
      ending_at: endingDate.isValid ? endingDate.toFormat('TT') : '20:00:00',
      selectedTickets: selectedTickets ?? ['*'],
      description: eventDate?.description ?? '',
    },
    validate: {
      title: isNotEmpty('Field cannot be empty'),
      date: isNotEmpty('Field cannot be empty'),
      starting_at: isNotEmpty('Field cannot be empty'),
      ending_at: isNotEmpty('Field cannot be empty'),
    },
    enhanceGetInputProps: (payload) => ({
      readOnly: campaignSettings.isCampaignReadOnly && payload.options.type === 'input',
    }),
  });

  useFormListValidator({ form, id: 'event_date' });

  return (
    <>
      <SimpleGrid cols={{ base: 1, md: 2 }}>
        <TextInput
          label="Create a title for this event date"
          placeholder="Enter event date title"
          required
          {...form.getInputProps('title')}
        />
        <Box />
        <DatePickerInput
          label="Date"
          placeholder="Select event date"
          minDate={new Date()}
          maxDate={
            campaign?.expires_at ? DateTime.fromISO(campaign?.expires_at)?.toJSDate() : undefined
          }
          required
          {...form.getInputProps('date')}
        />
        <TextInput
          label={
            <Group justify="space-between" align="center">
              <Text type="label">Timezone</Text>
              <Tooltip label="Timezone is determined from your browser settings. If your event is happening in different timezone, you may need to account for that.">
                <IconInfo width={16} />
              </Tooltip>
            </Group>
          }
          value={Intl.DateTimeFormat().resolvedOptions().timeZone}
          readOnly
        />
        <TimeInput label="Select a start time" required {...form.getInputProps('starting_at')} />
        <TimeInput label="Select an end time" required {...form.getInputProps('ending_at')} />
      </SimpleGrid>
      <Divider />
      <EventDateTicketsMultiSelect form={form} />
      <Divider />
      <AccordionCard
        title="Additional Settings"
        isCardCollapsed={false}
        description="Add event date description to provide more information about your event date."
      >
        <Textarea
          label="Event Date Description"
          placeholder="Enter date description"
          autosize
          minRows={3}
          maxRows={3}
          {...form.getInputProps('description')}
        />
      </AccordionCard>
    </>
  );
};

const AddEditEventDateFormWrapper = () => {
  const { causeId, eventId, dateId } = useAddEventDate();
  const { data: eventDate, isLoading } = useShowEventDate({
    causeId,
    eventId,
    dateId,
  });

  return (
    <>
      <LoadingOverlay visible={isLoading} />
      {!isLoading && <AddEditEventDateForm eventDate={eventDate?.event_date} />}
    </>
  );
};

export { AddEditEventDateFormWrapper };
